import type { SignoutTrigger } from "@/data/commands/auth/SignoutCmd";
import type { ISignupCmd } from "@/data/commands/auth/SignupCmd";
import type { IUsernamePasswordAuthCmd } from "@/data/commands/auth/UsernamePasswordAuthCmd";
import { Factory } from "@/data/dao/Factory";
import { CookieCache } from "@/data/dao/offline/CookieCache";
import { type IOfflineStorage, OfflineStorage } from "@/data/dao/offline/OfflineStorage";
import { AuthenticationDao } from "@/data/dao/service/AuthenticationDao";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import { ProfileDao } from "@/data/dao/service/ProfileDao";
import type { ITokenResult } from "@/data/model/Authorization";
import type { IProfile } from "@/data/model/Profile";
import { config, role } from "@/data/util/Config";
import { Signal, TypedSignal } from "@/data/util/Signal";



import { logError } from "@/shared/logging/errors";
import { logInformation, logWarning } from "@/shared/logging/informations";
import { DeviceFingerprintProvider } from "@/shared/services/DeviceFingerprintProvider";
import { setBearerToken } from "@/shared/utils/androidNativeUtils";
import { objectToArray } from "@/shared/utils/mappers";
import { serialize } from "@/shared/utils/serialization";



import { TastierClient, type TastierClientConfig } from "@/app/TastierClient";
import { PaymentMethodSelector } from "@/app/elements/PaymentMethodSelector";
import { appEvents } from "@/app/events/appEvents";
import { ExternalLogin } from "@/app/ui/ExternalLogin";
import { Popup } from "@/app/ui/Popup";


export class Identity {
    private authenticationDao = Factory.getInstance<AuthenticationDao>(AuthenticationDao);
    private profileDao = Factory.getInstance<ProfileDao>(ProfileDao);
    private customerDao = Factory.getInstance<CustomerDao>(CustomerDao);
    private cookieCache = Factory.getInstance<CookieCache>(CookieCache);

    private deviceHash?: string; // identifies user's device, should not be updated unless necessary
    private profile?: IProfile;

    static onIdentityChanged = new TypedSignal<boolean>();
    public onIdentityChange = new Signal();
    public onProfileChange = new Signal();

    public isSigningOut = false;

    public getProfile(): IProfile | undefined {
        return this.profile;
    }

    public getIdentityId(): string | undefined {
        return this.getProfile()?.identityId;
    }

    public hasIdentityEmail(): boolean {
        const profile = this.getProfile();
        if (profile == null)
            return false;

        return profile.email != null && profile.email.trim().length > 0;
    }

    public updateIdentityEmail(email: string) {
        if (this.profile != null) {
            this.profile.email = email;
        }
    }

    public getDeviceHash(): string | undefined {
        return this.deviceHash;
    }

    public isWaiterInCurrentLocation(): boolean {
        const currentAllocation = TastierClient.context.currentAllocation;
        if (!currentAllocation) return false;

        const locationId = currentAllocation.locationId;
        return (
            objectToArray(this.profile?.roles).includes(role.Operator) &&
            objectToArray(this.profile?.operatorLocations).includes(locationId)
        );
    }

    public isAdmin() {
        return objectToArray(this.profile?.roles).includes(role.Admin);
    }

    public isWaiterOrAdmin(): boolean {
        return this.isWaiterInCurrentLocation() || this.isAdmin();
    }

    public hasAccessToNonPortalReports(): boolean {
        return TastierClient.identityContext.getProfile()?.hasAccessToNonPortalReports === true;
    }

    public async init(config: TastierClientConfig): Promise<void> {
        logInformation("Init identification");

        try {
            //try to load previous state from cache
            if (await this.loadPreviousState()) {
                this.emitIdentityChangedEvent({ isLogout: false });
                return;
            }

            // if could not load previous state but the transaction id is provided - try to recover by transaction id
            if (config.transactionId) {
                await this.authenticateWithTransactionId(config.transactionId);
                return;
            }

            // proceed with standard anonymous auth mechanism
            await this.createAnonymousSession();
            await this.refreshUserProfile();
            this.emitIdentityChangedEvent({ isLogout: false });
        } catch (ex) {
            // could not get profile, try to renew auth and obtain profile again
            logError(ex);
            await this.renewAuth();
        }
    }

    public async authExternal(provider: string): Promise<void> {
        try {
            const tokenResult = await ExternalLogin.loginWithProvider(provider);
            await this.refreshStateAfterIdentityChange(tokenResult);
        } catch (ex) {
            logError(ex);
            throw ex;
        }
    }

    public async authWithUsernamePassword(cmd: IUsernamePasswordAuthCmd): Promise<void> {
        try {
            const tokenResult = await this.authenticationDao.auth(cmd);
            await this.refreshStateAfterIdentityChange(tokenResult);
        } catch (ex) {
            logError(ex);
            throw ex;
        }
    }

    public async signup(cmd: ISignupCmd): Promise<void> {
        try {
            const tokenResult = await this.authenticationDao.signup(cmd);
            await this.refreshStateAfterIdentityChange(tokenResult);
        } catch (ex) {
            logError(ex);
            throw ex;
        }
    }

    public async renewAuth(): Promise<void> {
        try {
            await this.createAnonymousSession();
            await this.refreshUserProfile();
            this.emitAllIdentityChangedEvents({ isLogout: false });
        } catch (ex) {
            logError(ex);
            throw new Error("Failed to renew authorization");
        }
    }

    public async signOut(options: { keepAllocation?: boolean; trigger: SignoutTrigger }): Promise<void> {
        if (this.isSigningOut) return;

        this.isSigningOut = true;
        try {
            // sign out from previous session
            await TastierClient.context.disconnectSocket();
            await this.authenticationDao.signOut({
                keepAllocation: options.keepAllocation ?? false,
                trigger: options.trigger,
            });
            this.resetIdentityState();

            // create new anonymous session using previous device - to bind anonymous user with logged out user
            await this.createAnonymousSession();
            await this.refreshUserProfile();

            await TastierClient.context.reInitAfterIdentityChange({ reloadAllocation: false });
            this.emitAllIdentityChangedEvents({ isLogout: true });
        } catch (ex) {
            logError(ex);
            throw new Error("Failed to renew authorization");
        } finally {
            this.isSigningOut = false;
        }
    }

    public aggregateIdentity(profile: IProfile, forceRefresh: boolean = false) {
        this.profile = profile;
        if (forceRefresh) {
            this.emitAllIdentityChangedEvents({ isLogout: false });
            return;
        }

        const hasChanged = serialize(this.getProfile()) !== serialize(profile);
        if (hasChanged) {
            this.emitAllIdentityChangedEvents({ isLogout: false });
        }
    }

    // validates if user token is still valid for current identity
    public async validateSession() {
        if (!this.hasPreviousSession()) {
            return true;
        }

        const serverIdentityId = await this.customerDao.getCurrentIdentityId();
        const localIdentityId = this.getIdentityId();

        return serverIdentityId == localIdentityId;
    }

    public isAuthenticated() {
        const profile = TastierClient.identityContext.getProfile();
        return profile?.authenticatedAs !== undefined;
    }

    private async loadPreviousState(): Promise<boolean> {
        logInformation("Load previous state...");
        try {
            if (!this.hasPreviousSession()) {
                logWarning("No previous state in cache.");
                return false;
            }

            const deviceHash = this.loadPreviousDeviceHash();
            if (!deviceHash) {
                logWarning("No device hash found in cache");
                return false;
            }

            logInformation("Loading user profile based on previous state.");
            this.profile = await this.profileDao.getProfile();
            this.deviceHash = deviceHash;

            logInformation("Previous state has been rebuilt.");
            return true;
        } catch (ex) {
            logError(ex);
            return false;
        }
    }

    private async authenticateWithTransactionId(transactionId: string) {
        try {
            await this.getSessionByTransactionId(transactionId);
            await this.refreshUserProfile();
            this.emitIdentityChangedEvent({ isLogout: false });
        } catch (ex) {
            logError(ex);
            Popup.show(`Could not restore user session for transaction:<br><strong>${transactionId}</strong>!`);
            throw new Error(`Could not restore user session for transaction: ${transactionId}!`);
        }
    }

    private async getSessionByTransactionId(transactionId: string) {
        logInformation(`Restoring session for transaction id: ${transactionId}`);
        const tokenResult = await this.authenticationDao.getSessionByTransactionId({
            transactionId: transactionId,
            deviceFingerprint: await DeviceFingerprintProvider.getFingerprint(),
        });
        await this.updateIdentityState(tokenResult);
    }

    private async createAnonymousSession(options?: { isResetSession: boolean }) {
        logInformation("Create anonymous session");

        const fingerprint = await DeviceFingerprintProvider.getFingerprint();
        const tokenResult = await this.authenticationDao.authAnonymous({
            fingerprint: fingerprint,
            deviceHash: this.getDeviceHash(),
            isResetSession: options?.isResetSession ?? false,
        });

        await this.updateIdentityState(tokenResult);
    }

    private async refreshStateAfterIdentityChange(tokenResult: ITokenResult) {
        await this.updateIdentityState(tokenResult);
        await this.refreshUserProfile();

        await TastierClient.context.reInitAfterIdentityChange({ reloadAllocation: true });
        this.emitAllIdentityChangedEvents({ isLogout: false });
    }

    public async refreshUserProfile() {
        this.profile = await this.profileDao.getProfile();
    }

    private emitAllIdentityChangedEvents(options: { isLogout: boolean }) {
        this.onIdentityChange.dispatch(options.isLogout);
        this.emitIdentityChangedEvent(options);
    }

    private emitIdentityChangedEvent(options: { isLogout: boolean }) {
        appEvents.identity.identityChanged.dispatch({
            isLogout: options.isLogout,
            profile: this.profile,
        });
    }

    private loadPreviousDeviceHash() {
        return this.cookieCache.readString(config.deviceHashCookieName);
    }

    private updateDeviceHash() {
        if (this.deviceHash) {
            this.cookieCache.writeString(config.deviceHashCookieName, this.deviceHash);
        } else {
            this.cookieCache.remove(config.deviceHashCookieName);
        }
    }

    private async updateIdentityState(newTokenResult: ITokenResult) {
        logInformation("Update identity state...");

        if (!newTokenResult) {
            logInformation("Token result is null - clear previous session state. This path should not be accessible.");
            this.resetIdentityState();
            return;
        }

        setBearerToken(newTokenResult.bearer);

        logInformation("Identity state object has been updated with new device hash. Save it in cache.");
        this.deviceHash = newTokenResult.deviceHash;
        this.updateDeviceHash();
    }

    private resetIdentityState() {
        logInformation("Reset identity state...");
        this.profile = undefined;

        setBearerToken(null);
    }

    private hasPreviousSession(): boolean {
        return this.cookieCache.readString(config.appSessionCookieName) != null;
    }

    public async resetAnonymousSession(): Promise<void> {
        if (this.isSigningOut) return;

        this.isSigningOut = true;
        try {
            // sign out from previous session
            await TastierClient.context.disconnectSocket();
            await this.authenticationDao.signOut({ keepAllocation: true, trigger: "kiosk-reset" });
            this.resetIdentityState();
            TastierClient.context.fulfillmentsLoaded = false;

            // delete personal and session cookies
            this.clearSessionAndPersonalCookies();

            // create new anonymous session without using previous device - to create totally new identity and session
            await this.createAnonymousSession({ isResetSession: true });
            await this.refreshUserProfile();

            await TastierClient.context.reInitAfterIdentityChange({ reloadAllocation: true });
            this.emitAllIdentityChangedEvents({ isLogout: true });
        } catch (ex) {
            logError(ex);
            throw new Error("Failed to renew authorization");
        } finally {
            this.isSigningOut = false;
        }
    }

    private clearSessionAndPersonalCookies() {
        const offlineCache = Factory.getInstance<IOfflineStorage>(OfflineStorage);
        offlineCache.remove(config.appSessionCookieName);
        offlineCache.remove(config.deviceHashCookieName);
        offlineCache.remove(config.tokenCookieName);
        offlineCache.remove(PaymentMethodSelector.pmtMethodCacheKey);
        offlineCache.remove(PaymentMethodSelector.pmtMethodTypeCacheKey);
    }
}
